import { useApolloClient, useQuery } from '@apollo/client';
import { useMelwaysInfo } from '@seek/melways-react';
import { useTranslations } from '@vocab/react';
import {
  Text,
  Stack,
  Heading,
  TextLink,
  IconNewWindow,
  Box,
  Columns,
  Column,
  Loader,
  IconDocument,
  IconPeople,
} from 'braid-design-system';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import { DownloadLink } from 'src/components/DownloadLink/DownloadLink';
import { MetaDataApplied } from 'src/components/MetaDataApplied/MetaDataApplied';
import { Notes } from 'src/components/Notes/Notes';
import { Salary } from 'src/components/Salary/Salary';
import { RESUME, COVER_LETTER } from 'src/const/app';
import {
  GET_APPLIED_JOB_DETAIL,
  GET_RESUME_LINK,
  GET_COVER_LETTER_LINK,
} from 'src/containers/JobsContainer/queries.graphql';
import { analyticsClient } from 'src/modules/analytics/analytics';
import type { AppliedJob, JobInsight, ApplicantCount } from 'src/types/graphql';
import { getJobStatus, navigateToUrl } from 'src/utils';

import translations from './ViewApplication.vocab';

interface Props {
  index: number;
  job: AppliedJob;
  link: string;
}

export const ViewApplication = ({ index, job, link }: Props) => {
  const client = useApolloClient();
  const { t } = useTranslations(translations);
  const notes = job.notes;
  const { isActive, isExternal } = job;
  const jobStatus = getJobStatus(isActive);
  const salaryLabel = job.job.salary?.label;
  const currencyLabel = job.job.salary?.currencyLabel;
  const advertiserLogo = job.job.products?.branding?.logo?.url ? (
    <img
      alt="advertiser logo"
      src={job.job.products?.branding?.logo?.url}
      width={150}
    />
  ) : null;
  const jobId = job?.id;
  const hasRoleRequirements = job.job.tracking?.hasRoleRequirements;
  const { locale } = useMelwaysInfo();
  const { data, loading } = useQuery(GET_APPLIED_JOB_DETAIL, {
    variables: { jobId, locale },
  });
  const isApplicantCount = (insight: any): insight is ApplicantCount =>
    insight.__typename === 'ApplicantCount';

  const { resume, coverLetter } = data?.viewer?.appliedJob?.documents || {};
  const resumeFilename = resume?.attachmentMetadata?.fileName || '';
  const coverLetterFilename = coverLetter?.attachmentMetadata?.fileName || '';
  const insights = data?.jobDetails.insights;
  const hasInsights = insights && insights.length > 0;
  const handleDownload = (documentType: string) => {
    const isResume = documentType === RESUME;
    analyticsClient.trackLink({
      eventName: 'document_download_pressed',
      jobId,
      jobListingDate: job.job.createdAt.dateTimeUtc,
      jobLocation: job.job.location.label,
      jobSalary: job.job.salary?.label,
      jobStatus,
      jobTitle: job.job.title,
      jobIsLinkOut: job.isExternal,
      advertiserName: job.job.advertiser.name,
      documentType,
      currentPage: 'my activity',
      currentPageSection: 'applied jobs',
      currentPageSubsection: 'view application',
    });

    client
      .query({
        fetchPolicy: 'no-cache',
        query: isResume ? GET_RESUME_LINK : GET_COVER_LETTER_LINK,
        variables: { jobId },
      })
      .then((res) => {
        navigateToUrl(
          res.data?.viewer?.appliedJob?.documents[
            isResume ? 'resume' : 'coverLetter'
          ].downloadLink,
        );
      });
  };

  const [ref, inView] = useInView({
    threshold: 0.5,
  });

  useEffect(() => {
    if (hasInsights && inView) {
      analyticsClient.trackView({
        eventName: 'applicant_insights_section_viewed',
        currentPageSubsection: 'view application',
        jobid: jobId,
        jobIsLinkOut: isExternal,
        jobHasRoleRequirements: hasRoleRequirements,
        jobStatus,
        jobApplicationStatus: job.events.slice(-1)[0].status,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  return (
    <Stack space="xlarge">
      <Box marginTop="medium">
        <Columns space="small" alignY="top" collapseBelow="tablet">
          {advertiserLogo && (
            <Column width="content">
              <Text tone="caution">{advertiserLogo}</Text>
            </Column>
          )}
          <Column>
            <Stack space="small">
              <Text weight="strong" tone="secondary">
                {t('Application for')}
              </Text>
              <Heading level="3">{job.job.title}</Heading>
              <Text weight="strong">{job.job.advertiser.name}</Text>
              <Text>{job.job.location.label}</Text>
              {salaryLabel ? (
                <Salary
                  salary={salaryLabel}
                  currencyLabel={currencyLabel ?? undefined}
                />
              ) : null}
              <Text>
                <TextLink
                  icon={<IconNewWindow />}
                  iconPosition="trailing"
                  href={link}
                  target="_blank"
                >
                  {t('View job')}
                </TextLink>
              </Text>
            </Stack>
          </Column>
        </Columns>
      </Box>

      <Stack space={{ mobile: 'large', tablet: 'medium' }}>
        <Text weight="strong">{t('Application status')}</Text>
        <MetaDataApplied
          isActive={isActive}
          isExternal={isExternal}
          appliedDate={job.appliedAt?.shortAbsoluteLabel}
          appliedDateHiddenVisually={job.appliedAt?.dateTimeUtc}
          events={job?.events}
        />
      </Stack>

      {Boolean(!isExternal) ? (
        <Stack space={{ mobile: 'large', tablet: 'medium' }}>
          <Text weight="strong">{t('Document submitted')}</Text>
          {loading ? (
            <Loader />
          ) : (
            <>
              <DownloadLink
                fileName={resumeFilename}
                label={RESUME}
                onClick={() => handleDownload(RESUME)}
                automation="job-item-resume"
              />
              <DownloadLink
                fileName={coverLetterFilename}
                label={COVER_LETTER}
                onClick={() => handleDownload(COVER_LETTER)}
                automation="job-item-cover-letter"
              />
            </>
          )}
        </Stack>
      ) : null}

      {hasInsights ? (
        <div ref={ref}>
          <Stack space={{ mobile: 'large', tablet: 'medium' }}>
            <Text weight="strong">{t('Application insights title')}</Text>
            {loading ? (
              <Loader />
            ) : (
              <Stack space="small">
                {insights.map((insight: JobInsight, key: number) => (
                  <Columns space="medium" alignY="center" key={key}>
                    <Column width="content">
                      {isApplicantCount(insight) ? (
                        <IconPeople />
                      ) : (
                        <IconDocument />
                      )}
                    </Column>
                    <Column>
                      <Text>{insight.message}</Text>
                    </Column>
                  </Columns>
                ))}
              </Stack>
            )}
          </Stack>
        </div>
      ) : null}

      {notes ? <Notes index={index} notes={notes} /> : null}
    </Stack>
  );
};
